import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  useAuthLoginClientCreateMutation,
  useAuthLoginProfessionalResponsableCreateMutation,
  useAuthRegisterClientCreateMutation,
  useAuthRegisterProfessionalResponsableCreateMutation,
  useAuthTokenRefreshCreateMutation,
  useAuthLogoutCreateMutation,
  useTeamsTeamMembersRetrieveQuery
} from '../services/GLOWClient';
import { axiosClient } from '../services/axiosClient';
import { store } from '../store';
import { GLOWApiClient } from '../apiClients/GLOWClient';
import { dashboardApi } from '../services/dashboardApi';
import { salonsApi } from '../services/salonsApi';
import { accessControlApi } from '../services/accessControlApi';
import { isNativeIosApp } from '../utils/nativePlatform';

// Create the auth context
const AuthContext = createContext();

export const postNativeAuthToken = (accessToken) => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (params.get('saloone_native') !== 'capacitor') return;
  const platform = params.get('saloone_platform');
  if (!['ios', 'android'].includes(platform)) return;
  const configured = (
    platform === 'ios'
      ? process.env.REACT_APP_NATIVE_IOS_ORIGIN
      : process.env.REACT_APP_NATIVE_ANDROID_ORIGIN
  )?.trim().replace(/\/$/, '');
  if (!configured) return;
  try {
    const parsed = new URL(configured);
    const exactOrigin = parsed.origin === 'null'
      ? `${parsed.protocol}//${parsed.host}`
      : parsed.origin;
    if (
      !parsed.host
      || parsed.username
      || parsed.password
      || parsed.search
      || parsed.hash
      || !['', '/'].includes(parsed.pathname)
      || exactOrigin !== configured
    ) return;
    window.parent.postMessage(
      { type: 'saloone.auth.changed', accessToken: accessToken || '' },
      exactOrigin,
    );
  } catch (_error) {
    // Invalid configured origins are ignored rather than widened.
  }
};

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamMemberId, setTeamMemberId] = useState(null);

  useEffect(() => {
    postNativeAuthToken(user?.token || '');
  }, [user?.token]);

  // RTK Query mutations
  const [refreshToken] = useAuthTokenRefreshCreateMutation();
  const [logoutMutation] = useAuthLogoutCreateMutation();
  const [loginClient] = useAuthLoginClientCreateMutation();
  const [loginProfessional] = useAuthLoginProfessionalResponsableCreateMutation();
  const [registerClient] = useAuthRegisterClientCreateMutation();
  const [registerProfessional] = useAuthRegisterProfessionalResponsableCreateMutation();

  // Initialize auth state from local storage
  useEffect(() => {
    // Clear any stale logout flag on mount
    sessionStorage.removeItem('is_logging_out');

    const initializeAuth = async () => {
      try {
        const storedUser = localStorage.getItem('user');

        if (storedUser) {
          const userData = JSON.parse(storedUser);

          // If we have a stored user, try to refresh the token
          try {
            // Send the refresh token to get a new access token
            const refreshResult = await refreshToken({
              tokenRefreshRequest: { refresh: userData.refresh_token }
            }).unwrap();

            // Update the token in the stored user data
            const updatedUser = {
              ...userData,
              token: refreshResult.access,
              // Update refresh token if the API returns a new one
              refresh_token: refreshResult.refresh || userData.refresh_token
            };

            localStorage.setItem('user', JSON.stringify(updatedUser));
            // Keep access token in a dedicated key used by baseQuery/axios
            if (refreshResult?.access) {
              localStorage.setItem('access_token', refreshResult.access);
            }
            if (refreshResult?.refresh) {
              localStorage.setItem('refresh_token', refreshResult.refresh);
            }
            setUser(updatedUser);
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            // Only clear if the token is actually invalid/expired
            // Don't log out for network errors or other temporary issues
            if (refreshError.status === 401) {
              localStorage.removeItem('user');
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              setUser(null);
            } else {
              // For other errors, keep the user logged in with existing token
              setUser(JSON.parse(storedUser));
            }
          }
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        setError('Failed to initialize authentication');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [refreshToken]);

  const googleLogin = async ({ idToken }, userType = 'client') => {
    try {
      setError(null);
      const response = await axiosClient.post('/auth/google/login/', {
        id_token: idToken,
        user_type: userType,
      });

      const data = response?.data;
      const userData = {
        ...data.user,
        token: data.access,
        refresh_token: data.refresh,
        userType
      };

      localStorage.setItem('user', JSON.stringify(userData));
      if (data?.access) localStorage.setItem('access_token', data.access);
      if (data?.refresh) localStorage.setItem('refresh_token', data.refresh);
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('AuthContext: Google login error:', err);
      const data = err?.response?.data;
      const message =
        (typeof data?.detail === 'string' && data.detail) ||
        (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]) ||
        'Google login failed';
      setError(message);
      throw err;
    }
  };

  const googleLink = async ({ idToken }) => {
    try {
      setError(null);
      const response = await axiosClient.post('/auth/google/link/', {
        id_token: idToken,
      });
      return response?.data;
    } catch (err) {
      console.error('AuthContext: Google link error:', err);
      const data = err?.response?.data;
      const message =
        (typeof data?.detail === 'string' && data.detail) ||
        (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]) ||
        'Google link failed';
      setError(message);
      throw err;
    }
  };

  // Login function
  const login = async (credentials, userType = 'client') => {
    try {
      setError(null);
      let response;

      if (userType === 'client') {
        response = await loginClient({ loginRequest: credentials }).unwrap();
      } else {
        response = await loginProfessional({ loginRequest: credentials }).unwrap();
      }

      const userData = {
        ...response.user,
        token: response.access,
        refresh_token: response.refresh,
        userType
      };

      localStorage.setItem('user', JSON.stringify(userData));
      // Store tokens in dedicated keys used by api clients
      if (response?.access) localStorage.setItem('access_token', response.access);
      if (response?.refresh) localStorage.setItem('refresh_token', response.refresh);
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('AuthContext: Login error:', err);
      const data = err?.data;
      const message =
        (typeof data?.detail === 'string' && data.detail) ||
        (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]) ||
        'Login failed';
      setError(message);
      throw err;
    }
  };

  // Register function
  const register = async (userData, userType = 'client') => {
    if (userType !== 'client' && isNativeIosApp()) {
      const policyError = new Error('Professional registration is unavailable in the iOS app.');
      setError(policyError.message);
      throw policyError;
    }

    try {
      setError(null);
      let response;

      if (userType === 'client') {
        response = await registerClient({ registerRequest: userData }).unwrap();
      } else {
        response = await registerProfessional({ registerRequest: userData }).unwrap();
      }

      const authData = {
        ...response.user,
        token: response.access,
        refresh_token: response.refresh,
        userType
      };

      localStorage.setItem('user', JSON.stringify(authData));
      // Store tokens in dedicated keys used by api clients
      if (response?.access) localStorage.setItem('access_token', response.access);
      if (response?.refresh) localStorage.setItem('refresh_token', response.refresh);
      setUser(authData);

      return authData;
    } catch (err) {
      console.error('Registration error:', err);
      const data = err?.data;
      const flattened = data && typeof data === 'object' ? Object.values(data).flat() : [];
      const first = flattened.find((v) => typeof v === 'string' && v.trim());
      const message =
        (typeof data?.detail === 'string' && data.detail) ||
        (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]) ||
        first ||
        (flattened.length ? String(flattened[0]) : '') ||
        'Registration failed';
      setError(message);
      throw err;
    }
  };

  // Logout function
  const logout = async () => {
    // Prevent multiple concurrent logout calls
    if (sessionStorage.getItem('is_logging_out')) return;
    sessionStorage.setItem('is_logging_out', 'true');

    try {
      // 1. Clear local UI state IMMEDIATELY to trigger navigation
      // This makes the UI feel responsive and stops background queries from using stale state
      postNativeAuthToken('');
      setUser(null);

      // 2. Clear tokens and user data from storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');

      // 3. Reset RTK Query caches
      try {
        store.dispatch(GLOWApiClient.util.resetApiState());
        store.dispatch(dashboardApi.util.resetApiState());
        store.dispatch(salonsApi.util.resetApiState());
        store.dispatch(accessControlApi.util.resetApiState());
      } catch (e) {
        console.warn('Failed to reset API caches on logout', e);
      }

      // 4. Attempt API logout in background
      // We don't 'await' it here to not block the UI transition
      logoutMutation().unwrap().catch(err => {
        console.warn('Background API logout call failed:', err);
      });

    } catch (err) {
      console.error('Logout process error:', err);
    } finally {
      // 5. Unset the logging_out flag after a delay to protect 
      // against any late-arriving 401s from background requests
      setTimeout(() => {
        sessionStorage.removeItem('is_logging_out');
      }, 2000);
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!user;
  };

  // Get user type
  const getUserType = () => {
    return user?.userType || null;
  };

  // Function to fetch team member ID for the current user
  const fetchTeamMemberId = async () => {
    if (!user || user.userType !== 'employee') {
      setTeamMemberId(null);
      return null;
    }

    try {
      // Use the API to find the team member associated with the current user
      const response = await axiosClient.get('/teams/team-members/', {
        params: { user_id: user.id },
      });

      const data = response?.data;

      // If we found a team member for this user, store the ID
      if (data && data.length > 0) {
        const id = data[0].id;
        setTeamMemberId(id);
        return id;
      } else {
        setTeamMemberId(null);
        return null;
      }
    } catch (err) {
      console.error('Error fetching team member ID:', err);
      setTeamMemberId(null);
      return null;
    }
  };

  // Fetch team member ID when user changes
  useEffect(() => {
    if (user && ['professional_employe', 'professional_manager_employee'].includes(user.role)) {
      fetchTeamMemberId();
    } else {
      setTeamMemberId(null);
    }
  }, [user]);

  // Check if user is an employee
  const isEmployee = () => {
    return ['professional_employe', 'professional_manager_employee'].includes(user?.role);
  };

  // Update user data (e.g., after profile changes)
  const updateUser = (updatedData) => {
    const updatedUser = {
      ...user,
      ...updatedData
    };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    login,
    googleLogin,
    googleLink,
    register,
    logout,
    isAuthenticated,
    getUserType,
    teamMemberId,
    isEmployee,
    fetchTeamMemberId,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
